import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface SOP {
  id: string;
  title: string;
  status: string;
}

interface StartRunDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  userId: string;
  onSuccess: () => void;
}

export function StartRunDialog({
  open,
  onOpenChange,
  organizationId,
  userId,
  onSuccess,
}: StartRunDialogProps) {
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [sops, setSops] = useState<SOP[]>([]);
  const [selectedSopId, setSelectedSopId] = useState("");
  const [runName, setRunName] = useState("");
  const [dueDate, setDueDate] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      fetchSOPs();
      setSelectedSopId("");
      setRunName("");
      setDueDate("");
    }
  }, [open, organizationId]);

  const fetchSOPs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("sop_templates")
        .select("id, title, status")
        .eq("organization_id", organizationId)
        .in("status", ["ACTIVE", "DRAFT"])
        .order("title");

      if (error) throw error;
      setSops(data || []);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    if (!selectedSopId || !runName.trim()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please select an SOP and enter a run name",
      });
      return;
    }

    setStarting(true);
    try {
      // Create the run
      const { data: run, error: runError } = await supabase
        .from("sop_runs")
        .insert({
          name: runName,
          sop_template_id: selectedSopId,
          organization_id: organizationId,
          created_by_user_id: userId,
          status: "IN_PROGRESS",
          started_at: new Date().toISOString(),
          due_at: dueDate ? new Date(dueDate).toISOString() : null,
        })
        .select()
        .single();

      if (runError) throw runError;

      // Fetch SOP steps to create run steps
      const { data: steps, error: stepsError } = await supabase
        .from("sop_steps")
        .select("*")
        .eq("sop_template_id", selectedSopId)
        .order("order_index");

      if (stepsError) throw stepsError;

      // Create run steps from SOP steps
      if (steps && steps.length > 0) {
        const runSteps = steps.map((step) => ({
          sop_run_id: run.id,
          sop_step_id: step.id,
          title_snapshot: step.title,
          instruction_snapshot: step.instruction_rich_text,
          step_type_snapshot: step.step_type,
          required_proof_type_snapshot: step.required_proof_type,
          is_critical_snapshot: step.is_critical,
          status: "NOT_STARTED" as const,
          due_at: step.sla_minutes && dueDate
            ? new Date(new Date(dueDate).getTime() - (steps.length - step.order_index) * step.sla_minutes * 60000).toISOString()
            : null,
        }));

        const { error: runStepsError } = await supabase
          .from("sop_run_steps")
          .insert(runSteps);

        if (runStepsError) throw runStepsError;
      }

      toast({
        title: "Run Started",
        description: "Your SOP run has been started successfully.",
      });

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start New Run</DialogTitle>
          <DialogDescription>
            Select an SOP and start a new execution run
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="sop">Select SOP *</Label>
              <Select value={selectedSopId} onValueChange={setSelectedSopId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an SOP" />
                </SelectTrigger>
                <SelectContent>
                  {sops.length === 0 ? (
                    <SelectItem value="none" disabled>
                      No SOPs available
                    </SelectItem>
                  ) : (
                    sops.map((sop) => (
                      <SelectItem key={sop.id} value={sop.id}>
                        {sop.title} ({sop.status})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Run Name *</Label>
              <Input
                id="name"
                value={runName}
                onChange={(e) => setRunName(e.target.value)}
                placeholder="e.g., New Employee Onboarding - John Smith"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="due">Due Date (optional)</Label>
              <Input
                id="due"
                type="datetime-local"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleStart} disabled={starting || sops.length === 0}>
                {starting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Start Run
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
